import { clearCollectibleOwnedCountsCache } from "@/composables/collectibles/useCollectibleOwnedCounts";
import { useUserSettings } from "@/composables/user/userSettings";
import { fetchUserSettings } from "@/services/user/settings/userSettingsApi";

// Bulk flows call the marketplace mutation once per item; debouncing the settings refetch collapses
// such a burst into a single request that runs shortly after the last change settles.
const USER_SETTINGS_REFRESH_DEBOUNCE_MS = 400;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Re-fetch the seller's settings so `canChangeCurrency` reflects their current marketplace state
 * (it depends on whether they have any listed/withdrawn/sold items). Only that field is updated,
 * to avoid clobbering other, locally-edited settings.
 */
async function refreshCanChangeCurrency(): Promise<void> {
  try {
    const { collector } = await fetchUserSettings();
    const { userSettings } = useUserSettings();
    if (userSettings.value !== undefined) {
      userSettings.value.canChangeCurrency = collector.canChangeCurrency;
    }
  } catch {
    // The mutation itself already succeeded — a failed settings refresh must not surface as an
    // error. `canChangeCurrency` will catch up on the next successful change or page load.
  }
}

/**
 * Run after every successful seller-side marketplace item change (list, edit, withdraw, renew).
 * Keeps derived user state in sync: it invalidates the cached collectible owned/listed counts and
 * refreshes `canChangeCurrency`, which gates the display-currency setting and the profile-currency
 * hint shown on marketplace price inputs.
 *
 * Fire-and-forget: the settings refresh is debounced and runs in the background, so callers don't
 * await it and a bulk operation triggers only one request.
 */
export function afterMarketplaceItemChange(): void {
  clearCollectibleOwnedCountsCache();

  if (refreshTimer !== undefined) {
    clearTimeout(refreshTimer);
  }
  refreshTimer = setTimeout(() => {
    refreshTimer = undefined;
    void refreshCanChangeCurrency();
  }, USER_SETTINGS_REFRESH_DEBOUNCE_MS);
}
